import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, ChevronUp, ChevronDown, Save, Radio } from "lucide-react";
import { toast } from "sonner";
import { api, adminApi } from "../../lib/api";
import { ImageField, Field } from "./ImageField";

const parseTwitch = (url) => {
  const m = (url || "").match(/twitch\.tv\/([A-Za-z0-9_]+)/i);
  return m ? m[1].toLowerCase() : null;
};

export const StreamersEditor = ({ twitchConfigured }) => {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get("/streamers-live");
    setItems(data.streamers);
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = (id, key, val) => setItems((p) => p.map((it) => {
    if (it.id !== id) return it;
    const next = { ...it, [key]: val };
    if (key === "url") next.twitch = parseTwitch(val) || next.twitch;
    return next;
  }));

  const add = async () => {
    try { await adminApi.post("/streamers", { name: "New Streamer", platform: "Twitch", url: "", image: "" }); load(); toast.success("Streamer added"); }
    catch { toast.error("Add failed"); }
  };
  const save = async (it) => {
    try { await adminApi.put(`/streamers/${it.id}`, it); toast.success("Saved"); } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => { try { await adminApi.delete(`/streamers/${id}`); load(); } catch { toast.error("Delete failed"); } };
  const move = async (idx, dir) => {
    const next = [...items]; const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    try { await adminApi.post("/streamers/reorder", next.map((i) => i.id)); } catch { toast.error("Reorder failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-[#8F877C] text-sm">Add creators by URL. {twitchConfigured ? "Twitch channels auto-detect LIVE." : "Toggle \u201cLive now\u201d manually (or add Twitch keys for auto-detect)."}</p>
        <button onClick={add} data-testid="streamer-add-btn" className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal text-obsidian px-4 py-2.5 hover:bg-bone transition-colors"><Plus className="h-4 w-4" /> Add</button>
      </div>
      {items.map((it, idx) => {
        const twitchAuto = twitchConfigured && it.twitch;
        return (
          <div key={it.id} data-testid={`streamer-edit-${idx}`} className="border border-[#2A201A] bg-surface/40 p-5 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name" value={it.name} onChange={(v) => patch(it.id, "name", v)} />
              <Field label="Platform" value={it.platform} onChange={(v) => patch(it.id, "platform", v)} placeholder="Twitch / YouTube / Kick" />
            </div>
            <Field label="Channel URL" value={it.url} onChange={(v) => patch(it.id, "url", v)} placeholder="https://twitch.tv/yourname" />
            <ImageField label="Avatar" value={it.image} onChange={(v) => patch(it.id, "image", v)} />
            <label className={`flex items-center gap-3 ${twitchAuto ? "opacity-40" : ""}`}>
              <input type="checkbox" checked={!!it.live} disabled={twitchAuto} onChange={(e) => patch(it.id, "live", e.target.checked)}
                className="h-4 w-4 accent-[color:rgb(var(--brand-teal))]" />
              <span className="flex items-center gap-2 text-sm text-bone"><Radio className="h-4 w-4 text-destructive" /> Live now {twitchAuto && "(auto via Twitch)"}</span>
            </label>
            <div className="flex items-center gap-2">
              <button onClick={() => save(it)} className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal/90 text-obsidian px-4 py-2 hover:bg-teal"><Save className="h-4 w-4" /> Save</button>
              <button onClick={() => move(idx, -1)} className="p-2 border border-[#2A201A] hover:border-teal hover:text-teal transition-colors"><ChevronUp className="h-4 w-4" /></button>
              <button onClick={() => move(idx, 1)} className="p-2 border border-[#2A201A] hover:border-teal hover:text-teal transition-colors"><ChevronDown className="h-4 w-4" /></button>
              <button onClick={() => remove(it.id)} className="ml-auto p-2 border border-[#2A201A] text-[#8F877C] hover:border-destructive hover:text-destructive transition-colors"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
